import { z } from 'zod';
import { UserResponseSchema } from './user.js';
import { OrganizationResponseSchema } from './organization.js';
import { MembershipSchema, MembershipWithOrgSchema } from './membership.js';
// =============================================================================
// API Types - Request/Response types for REST API
// =============================================================================
// =============================================================================
// Auth API Types
// =============================================================================
/**
 * GET /api/v1/auth/me response
 */
export const AuthMeResponseSchema = z.object({
    user: UserResponseSchema,
    activeOrganization: OrganizationResponseSchema,
    membership: MembershipSchema,
});
/**
 * POST /api/v1/auth/login response
 */
export const AuthLoginResponseSchema = z.object({
    message: z.string(),
    phone: z.string(),
});
/**
 * POST /api/v1/auth/verify response
 */
export const AuthVerifyResponseSchema = z.object({
    user: UserResponseSchema,
    activeOrganization: OrganizationResponseSchema,
});
/**
 * POST /api/v1/auth/signup response
 */
export const AuthSignupResponseSchema = z.object({
    user: UserResponseSchema,
    organization: OrganizationResponseSchema,
    message: z.string(),
});
// =============================================================================
// Organization API Types
// =============================================================================
/**
 * GET /api/v1/organizations response
 */
export const OrganizationsListResponseSchema = z.object({
    organizations: z.array(MembershipWithOrgSchema),
});
/**
 * POST /api/v1/organizations/:id/switch response
 */
export const SwitchOrganizationResponseSchema = z.object({
    activeOrganization: OrganizationResponseSchema,
    membership: MembershipSchema,
});
// =============================================================================
// DocuSign API Types
// =============================================================================
/**
 * GET /api/v1/docusign/connect response
 */
export const DocuSignConnectResponseSchema = z.object({
    authUrl: z.string().url(),
});
/**
 * GET /api/v1/docusign/status response
 */
export const DocuSignStatusResponseSchema = z.object({
    connected: z.boolean(),
    accountId: z.string().optional(),
    accountName: z.string().optional(),
    expiresAt: z.string().datetime().optional(),
});
/**
 * POST /api/v1/docusign/send request
 */
export const DocuSignSendRequestSchema = z.object({
    documentIds: z.array(z.string()).min(1),
    recipients: z.object({
        buyerIds: z.array(z.string()).default([]),
        sellerIds: z.array(z.string()).default([]),
        includeCurrentUserAsAgent: z.boolean().default(true),
    }),
    envelope: z.object({
        subject: z.string().min(1),
        message: z.string().optional(),
    }),
});
/**
 * POST /api/v1/docusign/send response
 */
export const DocuSignSendResponseSchema = z.object({
    jobId: z.string().uuid(),
    status: z.literal('queued'),
    documentIds: z.array(z.string()),
});
// =============================================================================
// DocuSign Embedded Sending API Types (Phase 2)
// =============================================================================
/**
 * Summary of how AI-detected signature candidates were mapped to DocuSign
 * tabs — surfaced in the prepare response so the web UI can warn the agent
 * before opening DocuSign instead of only logging server-side (2026-08
 * incident: 39 of 127 tabs vanished silently). Mirrors
 * envelope.service.ts#TabMappingSummary.
 */
export const DocuSignTabSummarySchema = z.object({
    placed: z.number().int().nonnegative(),
    wrapped: z.number().int().nonnegative(),
    skippedNoRecipient: z.number().int().nonnegative(),
    skippedNoCoords: z.number().int().nonnegative(),
    skippedUnresolvable: z.number().int().nonnegative(),
    skippedByRole: z.record(z.string(), z.number().int().nonnegative()),
    /** Documents with no signature detection — none of their fields can be placed. Optional/additive. */
    documentsWithoutAnalysis: z.array(z.string()).optional(),
    /** Pages where a co-buyer/co-seller has fewer tabs than the other. Optional/additive. */
    partyGaps: z
        .array(z.object({
        documentId: z.string(),
        documentName: z.string().optional(),
        page: z.number().int().positive(),
        tabType: z.enum(['SIGN_HERE', 'INITIAL_HERE', 'DATE_SIGNED']),
        role: z.string(),
        missing: z.number().int().nonnegative(),
        added: z.number().int().nonnegative(),
    }))
        .optional(),
});
/**
 * POST /api/v1/docusign/prepare request
 * Prepare an envelope for agent review before sending
 */
export const DocuSignPrepareRequestSchema = z.object({
    propertyId: z.string().min(1, 'Property ID is required'),
    documentIds: z.array(z.string()).min(1, 'At least one document is required'),
    recipients: z.object({
        buyerContactIds: z.array(z.string()).optional().default([]),
        sellerContactIds: z.array(z.string()).optional().default([]),
        /**
         * Agent contacts (BUYER_AGENT, LISTING_AGENT, etc.) to include as signers.
         * These will be assigned AGENT_BUYER or AGENT_SELLER roles based on their
         * party role in the property.
         */
        agentContactIds: z.array(z.string()).optional().default([]),
        agentAsRecipient: z.boolean().optional().default(false),
    }),
    envelope: z.object({
        subject: z.string().min(1, 'Subject is required'),
        message: z.string().optional(),
    }),
});
/**
 * POST /api/v1/docusign/prepare response
 * Returns URL to DocuSign sender view for agent review
 */
export const DocuSignPrepareResponseSchema = z.object({
    success: z.literal(true),
    senderViewUrl: z.string().url(),
    envelopeId: z.string(),
    documentCount: z.number().int().positive(),
    recipientCount: z.number().int().positive(),
    // Additive (Task 9) — optional so older clients ignore it safely.
    tabSummary: DocuSignTabSummarySchema.optional(),
});
/**
 * POST /api/v1/docusign/preflight request
 * Same inputs as /prepare minus the envelope email block. Runs the identical
 * document/recipient resolution and tab mapping that /prepare runs, but stops
 * BEFORE any DocuSign call or database write, so the web UI can decide in
 * milliseconds whether to show the "fields will NOT be pre-placed" warning
 * without opening a DocuSign tab or creating an orphaned draft envelope.
 */
export const DocuSignPreflightRequestSchema = DocuSignPrepareRequestSchema
    .omit({ envelope: true })
    .strict();
/**
 * POST /api/v1/docusign/preflight response
 */
export const DocuSignPreflightResponseSchema = z.object({
    success: z.literal(true),
    documentCount: z.number().int().positive(),
    recipientCount: z.number().int().positive(),
    tabSummary: DocuSignTabSummarySchema,
});
/**
 * Callback event types from DocuSign sender view
 */
export const DocuSignCallbackEventEnum = z.enum([
    'send', // Agent clicked Send
    'save', // Agent clicked Save and Close
    'cancel', // Agent clicked Discard/Cancel
    'decline', // Agent declined
    'error', // Error occurred
]);
/**
 * GET /api/v1/docusign/send-callback query params
 * Callback from DocuSign after agent finishes in sender view
 */
export const DocuSignSendCallbackQuerySchema = z.object({
    envelopeId: z.string().min(1, 'Envelope ID is required'),
    event: DocuSignCallbackEventEnum.optional(),
});
// =============================================================================
// Generic API Response Types
// =============================================================================
/**
 * Generic success response
 */
export const SuccessResponseSchema = z.object({
    success: z.literal(true),
    message: z.string().optional(),
});
/**
 * Generic error response
 */
export const ErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.unknown()).optional(),
    }),
});
/**
 * Health check response
 */
export const HealthCheckResponseSchema = z.object({
    status: z.enum(['healthy', 'unhealthy']),
    timestamp: z.string().datetime(),
    version: z.string(),
    services: z.object({
        database: z.enum(['connected', 'disconnected']),
        redis: z.enum(['connected', 'disconnected', 'not_configured']).optional(),
    }),
});
//# sourceMappingURL=api-types.js.map